'use client'

import React from 'react'
import { useCheckout } from '@/lib/hooks'

import {
  CollectPayment,
  SelectCustomer,
  SelectPaymentMethod,
} from '@/components/molecules'
import { LoadingDots } from '@/components/ui'

type RenderStepProps = {
  Component: React.ComponentType<{ isCusRequired?: boolean }>
  isCusRequired?: boolean
}

export const CheckoutSteps = ({
  isCusRequired,
  isLoading,
}: {
  isCusRequired: boolean
  isLoading?: boolean
}) => {
  const { currentStep, fully_paid } = useCheckout()

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-10">
        <LoadingDots />
      </div>
    )
  }

  if (fully_paid) {
    return <RenderStep Component={CollectPayment} />
  }

  switch (currentStep) {
    case 'SELECT_CUSTOMER':
      return isCusRequired ? (
        <RenderStep Component={SelectCustomer} />
      ) : (
        <RenderStep
          Component={SelectPaymentMethod}
          isCusRequired={isCusRequired}
        />
      )
    case 'SELECT_PAYMENT_METHOD':
      return (
        <RenderStep
          Component={SelectPaymentMethod}
          isCusRequired={isCusRequired}
        />
      )
    case 'COLLECT_PAYMENT':
      return <RenderStep Component={CollectPayment} />
    default:
      return isCusRequired ? (
        <RenderStep Component={SelectCustomer} />
      ) : (
        <RenderStep
          Component={SelectPaymentMethod}
          isCusRequired={isCusRequired}
        />
      )
  }
}

const RenderStep: React.FC<RenderStepProps> = ({
  Component,
  isCusRequired,
}) => {
  const { setPaymentsLoading, getPayments, queryParams, currentStep } =
    useCheckout()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    setPaymentsLoading(true)
    if (queryParams?.cartId && currentStep === 'COLLECT_PAYMENT') {
      getPayments({ cartId: queryParams?.cartId })
    }
  }, [currentStep])

  return (
    <div className="flex-flow flex h-full w-full max-w-[600px] flex-col">
      <Component isCusRequired={isCusRequired} />
    </div>
  )
}
